// Global type definitions
declare global {
  interface Window {
    AndroidInterface?: {
      onSurveyCompleted?: (data: string) => void;
    };
  }
}

// External dependencies
import confetti from 'canvas-confetti';

// Styles
import './style.css';

// Types
import type { URLParams } from './types';

// Services
import { 
  RouterService,
  SurveyController,
  EventManager,
  UIRendererService,
  UrlParamService
} from './services';

// Utils
import { DomHelper, Logger } from './utils';

// Components
import { QuestionRenderer, SurveyUIBuilder, AnswerPreview } from './components';
import { DocumentationRenderer } from './DocumentationRenderer';

/**
 * Main Survey Application Class
 * Handles routing, initialization, and coordination of survey components
 */
class SurveyApp {
  private urlParams: URLParams = {};
  private appElement: HTMLDivElement;
  private surveyController?: SurveyController;
  private eventManager?: EventManager;
  private hasInitializationError: boolean = false;

  constructor() {
    this.appElement = DomHelper.getElement<HTMLDivElement>('#app')!;
    this.urlParams = UrlParamService.parseUrlParams();
    this.initialize();
  }

  private async initialize(): Promise<void> {
    const routeDecision = RouterService.determineRoute(
      this.urlParams, 
      window.location.pathname, 
      window.location.hash
    );

    try {
      switch (routeDecision.route) {
        case 'documentation':
          this.renderDocumentation();
          break;
        case 'landing':
          await RouterService.loadLandingPage();
          break;
        case 'survey':
          await this.initializeSurveyFlow();
          break;
      }
    } catch (error) {
      Logger.error('Failed to initialize app:', error);
      this.appElement.innerHTML = UIRendererService.renderErrorState('Failed to load application', 500);
    }
  }

  private async initializeSurveyFlow(): Promise<void> {
    this.surveyController = new SurveyController(this.urlParams, this.appElement);
    this.eventManager = new EventManager(
      this.surveyController.getStateManager(),
      () => this.render(),
      () => this.handleSurveySubmission()
    );

    try {
      await this.surveyController.initializeSurvey();
      this.render();
    } catch (error) {
      // SurveyController already handled the error display
      this.hasInitializationError = true;
    }
  }

  private renderDocumentation(): void {
    this.appElement.innerHTML = DocumentationRenderer.renderApiDocumentation();
  }

  private render(): void {
    if (!this.surveyController) return;
    
    // Don't render if there was an initialization error - keep the error message
    if (this.hasInitializationError) return;

    const stateManager = this.surveyController.getStateManager();
    
    if (!stateManager.getCurrentSurvey()) {
      this.appElement.innerHTML = UIRendererService.renderErrorState('Survey data not loaded', 404);
      return;
    }

    if (stateManager.isShowingIntro()) {
      this.renderIntroPage();
    } else {
      this.renderQuestionPage();
    }
  }

  private renderIntroPage(): void {
    if (!this.surveyController || !this.eventManager) return;

    const stateManager = this.surveyController.getStateManager();
    
    if (!stateManager.getSurveyMetadata()) {
      stateManager.setShowingIntro(false);
      this.renderQuestionPage();
      return;
    }

    const startCharacter = `<div style="display: flex; justify-content: center; margin-bottom: 24px;"><img src="/billy_survey.png" alt="Start Survey" width="200" height="200" /></div>`;

    let devStatusDropdown = '';
    if (import.meta.env.DEV) {
      devStatusDropdown = this.buildDevStatusDropdown();
    }

    const sections = [
      devStatusDropdown,
      SurveyUIBuilder.buildSurveyStatisticsSection(this.surveyController.getFullSurveyData()),
      startCharacter,
      SurveyUIBuilder.buildSurveyInfoSection(
        stateManager.getSurveyMetadata()!.name, 
        stateManager.getSurveyMetadata()!.description
      ),
      `<div class="intro-actions">
        <button class="btn primary" id="start-survey-btn">Start Survey</button>
      </div>`
    ].filter(section => section !== '');
    
    this.appElement.innerHTML = SurveyUIBuilder.buildSurveyContainer(sections);
    this.eventManager.attachIntroListeners();

    if (import.meta.env.DEV) {
      this.attachDevDropdownListener();
    }
  }

  private renderQuestionPage(): void {
    if (!this.surveyController || !this.eventManager) return;

    const stateManager = this.surveyController.getStateManager();
    const question = stateManager.getCurrentQuestion();
    const questionHtml = QuestionRenderer.renderQuestion(question, stateManager.getAnswers()[question.id] || '');
    
    const sections = [
      SurveyUIBuilder.buildProgressSection(stateManager.getCurrentQuestionIndex(), stateManager.getCurrentSurvey()!.questions.length),
      SurveyUIBuilder.buildHeaderSection(stateManager.getCurrentSurvey()!.title, stateManager.getCurrentQuestionIndex()),
      SurveyUIBuilder.buildQuestionSection(question, questionHtml),
      SurveyUIBuilder.buildButtonSection(stateManager.getCurrentQuestionIndex(), stateManager.getCurrentSurvey()!.questions.length)
    ].filter(section => section !== '');
    
    this.appElement.innerHTML = SurveyUIBuilder.buildSurveyContainer(sections);
    this.eventManager.attachAllListeners();

    // Initialize grid question if present
    if (question.type === 'grid') {
      QuestionRenderer.initializeGrid();
    }
  }

  private async handleSurveySubmission(): Promise<void> {
    if (!this.surveyController) return;

    try {
      await this.surveyController.submitSurvey();
      this.showSuccessMessage();
    } catch (error) {
      Logger.error('Survey submission failed:', error);
      this.handleSubmissionError(error);
    }
  }

  private showSuccessMessage(): void {
    const isAnonymous = this.urlParams.anonymous || false;
    const referralCode = this.urlParams.referralCode;

    const surveyResponse = this.surveyController?.buildSurveyResponse();
    const questions = this.surveyController?.getStateManager().getCurrentSurvey()?.questions;

    // Get payout type and message from full survey data
    const fullSurveyData = this.surveyController?.getFullSurveyData();
    const payoutType = fullSurveyData?.questions?.payout_type;
    const payoutTypeMessage = fullSurveyData?.questions?.payout_type_message;
    const payoutTypeMessageTitle = fullSurveyData?.questions?.payout_type_message_title;

    this.appElement.innerHTML = SurveyUIBuilder.buildSuccessMessage(
      isAnonymous,
      referralCode,
      surveyResponse,
      questions,
      payoutType,
      payoutTypeMessage,
      payoutTypeMessageTitle
    );

    // Attach tab listeners for answer preview in development mode
    if (import.meta.env.DEV) {
      AnswerPreview.attachTabListeners();
    }

    this.triggerConfetti();

    if (this.surveyController) {
      this.surveyController.notifyAndroidApp();
    }
  }

  private triggerConfetti(): void {
    confetti({
      particleCount: 120,
      spread: 90,
      origin: { y: 0.6 },
    });
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.7 },
      });
    }, 500);
  }

  private handleSubmissionError(error?: any): void {
    if (!this.surveyController) return;

    // Log response data in development
    const responseData = this.surveyController.buildSurveyResponse();
    Logger.log('Survey response data:', responseData);
    
    let userMessage = 'Failed to submit survey. Your responses could not be saved.';
    
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
      } else if ((error as any).status === 400) {
        userMessage = 'Invalid survey data. Please review your responses and try again.';
      } else if ((error as any).status === 403) {
        userMessage = 'You are not authorized to submit this survey. Please check your credentials.';
      } else if ((error as any).status === 404) {
        userMessage = 'The survey submission endpoint was not found. Please contact support.';
      } else if ((error as any).status === 500) {
        userMessage = 'The server is experiencing issues. Please try again later.';
      } else if ((error as any).apiResponse?.message) {
        userMessage = (error as any).apiResponse.message;
      }
    }
    
    // Show error for any submission failure
    this.appElement.innerHTML = UIRendererService.renderSubmissionError(userMessage);
  }

  private buildDevStatusDropdown(): string {
    return `
      <div style="display: flex; justify-content: center; margin-bottom: 16px;">
        <select id="dev-status-dropdown" style="padding: 8px 16px; font-size: 1rem; border-radius: 6px; border: 1px solid #ccc;">
          <option value="">-- Show Status Page (DEV only) --</option>
          <option value="loading">Loading</option>
          <option value="notfound">Survey Not Found</option>
          <option value="usernotfound">User Not Found</option>
          <option value="alreadycompleted">Survey Already Completed</option>
          <option value="notavailable">Survey Not Available</option>
          <option value="expired">Survey Expired</option>
          <option value="error">Survey Error</option>
          <option value="connection">Connection Error</option>
          <option value="success">Survey Success</option>
        </select>
      </div>
    `;
  }

  private attachDevDropdownListener(): void {
    const dropdown = document.getElementById('dev-status-dropdown') as HTMLSelectElement;
    if (!dropdown) return;

    dropdown.addEventListener('change', () => {
      const val = dropdown.value;
      if (val === 'loading') {
        this.appElement.innerHTML = UIRendererService.renderLoadingState();
      } else if (val === 'notfound') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('Survey not found', 404);
      } else if (val === 'usernotfound') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('User not found', 403);
      } else if (val === 'alreadycompleted') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('User has already completed this survey', 409);
      } else if (val === 'notavailable') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('Survey not available', 403);
      } else if (val === 'expired') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('Survey expired', 410);
      } else if (val === 'error') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('Some error occurred', 400);
      } else if (val === 'connection') {
        this.appElement.innerHTML = UIRendererService.renderErrorState('Internal Server Error', 500);
      } else if (val === 'success') {
        const isAnonymous = this.urlParams.anonymous || false;
        const referralCode = this.urlParams.referralCode;
        const surveyResponse = this.surveyController?.buildSurveyResponse();
        const questions = this.surveyController?.getStateManager().getCurrentSurvey()?.questions;

        // Get payout type and message from full survey data
        const fullSurveyData = this.surveyController?.getFullSurveyData();
        const payoutType = fullSurveyData?.questions?.payout_type;
        const payoutTypeMessage = fullSurveyData?.questions?.payout_type_message;
        const payoutTypeMessageTitle = fullSurveyData?.questions?.payout_type_message_title;

        this.appElement.innerHTML = SurveyUIBuilder.buildSuccessMessage(
          isAnonymous,
          referralCode,
          surveyResponse,
          questions,
          payoutType,
          payoutTypeMessage,
          payoutTypeMessageTitle
        );

        // Attach tab listeners for answer preview in development mode
        AnswerPreview.attachTabListeners();

        this.triggerConfetti();
      }
    });
  }
}

// Initialize the app
new SurveyApp();
